import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Badge, Form, Row, Col, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { getTrips, updateTrip, getClients } from '../api';
import { format } from 'date-fns';
import { Search, Unlock, AlertCircle, Hash, Car, X, Link as LinkIcon } from 'lucide-react';
import SearchableSelect from './SearchableSelect';
import { toast } from 'react-toastify';

const TripAdminView = () => {
    const [trips, setTrips] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filtros
    const [searchId, setSearchId] = useState('');
    const [passengerName, setPassengerName] = useState('');
    const [selectedClient, setSelectedClient] = useState('');
    const [searchSettlementId, setSearchSettlementId] = useState(''); // Buscador por Nº de liquidación
    const [filterPrefactura, setFilterPrefactura] = useState(''); // '' = todos | 'con' = con prefactura | 'sin' = sin prefactura
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Modal Liberación
    const [showReleaseModal, setShowReleaseModal] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);

    // Estado para entrada manual de liquidación
    const [manualSettlements, setManualSettlement] = useState({}); // {tripId: value}
    const [manualInvoices, setManualInvoices] = useState({}); // {tripId: value}
    const [actionLoading, setActionLoading] = useState({}); // {tripId: bool}

    const loadInitialData = async () => {
        try {
            const c = await getClients();
            setClients(c);
        } catch (e) { console.error(e); }
    };

    const loadTrips = useCallback(async (isSearchAction = false) => {
        setLoading(true);
        try {
            // Si hay un Nº de liquidación, ese filtro tiene prioridad absoluta (ignora fechas y otros filtros)
            if (searchSettlementId.trim() !== '') {
                const data = await getTrips({
                    settlement_id: searchSettlementId.trim(),
                    limit: 500
                });
                setTrips(data);
            } else {
                const data = await getTrips({
                    search_term: searchId || undefined,
                    passenger_name: passengerName || undefined,
                    client_id: selectedClient || undefined,
                    start_date: isSearchAction ? startDate + 'T00:00:00' : undefined,
                    end_date: isSearchAction ? endDate + 'T23:59:59' : undefined,
                    limit: 50
                });
                setTrips(data);
            }
        } catch (e) { 
            toast.error("Error cargando reservas");
        }
        setLoading(false);
    }, [searchId, passengerName, selectedClient, searchSettlementId, startDate, endDate]);

    // Filtro local de prefactura (se aplica sobre los trips ya cargados)
    const filteredTrips = filterPrefactura === 'con'
        ? trips.filter(t => t.client_invoice_id != null)
        : filterPrefactura === 'sin'
            ? trips.filter(t => t.client_invoice_id == null)
            : trips;

    useEffect(() => { 
        loadInitialData();
        loadTrips(); 
    }, [loadTrips]);

    const handleRelease = async () => {
        if (!selectedTrip) return;
        setLoading(true);
        try {
            await updateTrip(selectedTrip.id, { 
                settlement_id: null, 
                client_invoice_id: null,
                admin_status: 'processed' 
            });
            toast.success(`Reserva #${selectedTrip.id} liberada.`);
            setShowReleaseModal(false);
            await loadTrips();
        } catch (e) {
            toast.error("Error al liberar la reserva");
        }
        setLoading(false);
    };

    const handleManualLink = async (tripId) => {
        const val = manualSettlements[tripId];
        if (!val || val === '') {
            toast.warning("Ingrese un número de liquidación válido.");
            return;
        }

        setActionLoading(prev => ({ ...prev, [tripId]: true }));
        try {
            await updateTrip(tripId, { settlement_id: parseInt(val) });
            toast.success(`Reserva #${tripId} vinculada a Liq #${val}`);
            
            // Limpiar input y estado
            setManualSettlement(prev => {
                const newState = {...prev};
                delete newState[tripId];
                return newState;
            });
            await loadTrips();
        } catch (e) {
            console.error("Error vinculando:", e);
            const msg = e.response?.data?.detail || "Error: Verifique que la liquidación exista y sea del mismo conductor.";
            // Alerta forzada para que no pase desapercibido
            alert(msg);
            toast.error(msg);
            loadTrips(); // Revertir visualmente
        }
        setActionLoading(prev => ({ ...prev, [tripId]: false }));
    };

    const handleManualInvoiceLink = async (tripId) => {
        const val = manualInvoices[tripId];
        if (!val || val === '') {
            toast.warning("Ingrese un número de pre-factura válido.");
            return;
        }

        setActionLoading(prev => ({ ...prev, [tripId]: true }));
        try {
            const payloadValue = /^\d+$/.test(val) ? parseInt(val) : val;
            await updateTrip(tripId, { client_invoice_id: payloadValue });
            toast.success(`Reserva #${tripId} vinculada a Prefactura #${val}`);
            
            // Limpiar input y estado
            setManualInvoices(prev => {
                const newState = {...prev};
                delete newState[tripId];
                return newState;
            });
            await loadTrips();
        } catch (e) {
            console.error("Error vinculando pre-factura:", e);
            const msg = e.response?.data?.detail || "Error: Verifique que la pre-factura exista y sea del mismo cliente.";
            alert(msg);
            toast.error(msg);
            loadTrips(); // Revertir visualmente
        }
        setActionLoading(prev => ({ ...prev, [tripId]: false }));
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-0">🛠 Panel Maestro de Reservas</h3>
                    <p className="text-muted small">Herramienta de control administrativo para vínculos de pago y facturación.</p>
                </div>
                <Badge bg="dark" className="p-2 px-3 shadow-sm">MODO SUPER-ADMIN</Badge>
            </div>

            <Card className="shadow-sm border-0 mb-4 overflow-hidden">
                <Card.Header className="bg-white py-3">
                    <Row className="g-3">
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-uppercase" style={{fontSize:'10px'}}>Buscar ID</Form.Label>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-light"><Hash size={14}/></InputGroup.Text>
                                <Form.Control 
                                    placeholder="ID..." 
                                    value={searchId} 
                                    onChange={e => setSearchId(e.target.value)} 
                                    onKeyPress={e => e.key === 'Enter' && loadTrips(true)}
                                    disabled={searchSettlementId.trim() !== ''}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-uppercase" style={{fontSize:'10px'}}>🔍 Nº Liquidación</Form.Label>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-primary text-white"><Hash size={14}/></InputGroup.Text>
                                <Form.Control 
                                    type="number"
                                    placeholder="Ej: 28" 
                                    value={searchSettlementId} 
                                    onChange={e => setSearchSettlementId(e.target.value)} 
                                    onKeyPress={e => e.key === 'Enter' && loadTrips(true)}
                                    style={{ fontWeight: searchSettlementId ? '700' : 'normal', borderColor: searchSettlementId ? '#0d6efd' : '' }}
                                />
                            </InputGroup>
                            {searchSettlementId.trim() !== '' && (
                                <small className="text-primary fw-bold" style={{fontSize:'10px'}}>⚡ Ignora todos los demás filtros</small>
                            )}
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-uppercase" style={{fontSize:'10px'}}>Cliente</Form.Label>
                            <SearchableSelect 
                                options={clients} 
                                value={selectedClient} 
                                onChange={setSelectedClient} 
                                placeholder="Filtrar por Cliente..."
                                disabled={searchSettlementId.trim() !== ''}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-uppercase" style={{fontSize:'10px'}}>Desde</Form.Label>
                            <Form.Control type="date" size="sm" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={searchSettlementId.trim() !== ''} />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-uppercase" style={{fontSize:'10px'}}>Hasta</Form.Label>
                            <Form.Control type="date" size="sm" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={searchSettlementId.trim() !== ''} />
                        </Col>
                        <Col md={2} className="d-flex align-items-end gap-2">
                            <Form.Select 
                                size="sm" 
                                value={filterPrefactura} 
                                onChange={e => setFilterPrefactura(e.target.value)}
                                disabled={searchSettlementId.trim() !== ''}
                                style={{ fontWeight: filterPrefactura ? '700' : 'normal', borderColor: filterPrefactura ? '#6f42c1' : '' }}
                            >
                                <option value="">- Prefactura -</option>
                                <option value="con">✅ Con Prefactura</option>
                                <option value="sin">⏳ Sin Prefactura</option>
                            </Form.Select>
                        </Col>
                        <Col md={2} className="d-flex align-items-end gap-2">
                            <Button variant="primary" size="sm" className="flex-grow-1 fw-bold" onClick={() => loadTrips(true)} disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : <><Search size={14} className="me-2" /> Buscar</>}
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => { setSearchId(''); setPassengerName(''); setSelectedClient(''); setSearchSettlementId(''); setFilterPrefactura(''); loadTrips(); }}>
                                <X size={14} />
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                
                <div className="table-responsive" style={{maxHeight: '65vh'}}>
                    <Table hover className="mb-0 align-middle" style={{fontSize: '12px'}}>
                        <thead className="bg-light sticky-top" style={{zIndex: 10}}>
                            <tr className="text-uppercase text-muted" style={{fontSize:'10px'}}>
                                <th className="px-3">ID</th>
                                <th>Fecha / Cliente</th>
                                <th>Pasajero / Móvil</th>
                                <th>Producción</th>
                                <th>Liquidación</th>
                                <th>Prefactura</th>
                                <th className="text-end px-3">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTrips.map(t => (
                                <tr key={t.id} className={t.settlement_id ? 'bg-light bg-opacity-50' : ''}>
                                    <td className="px-3 fw-bold text-primary" style={{fontSize:'14px'}}>#{t.id}</td>
                                    <td>
                                        <div className="fw-bold">{format(new Date(t.scheduled_time), 'dd/MM/yyyy HH:mm')}</div>
                                        <div className="text-muted">{t.client?.name}</div>
                                    </td>
                                    <td>
                                        <div className="fw-bold">{t.passenger_name}</div>
                                        <div className="d-flex align-items-center gap-1 text-muted">
                                            <Car size={12}/> {t.vehicle?.internal_code || 'S/M'} - {t.driver?.full_name?.split(' ')[0]}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fw-bold text-dark">
                                            ${Math.round(t.provider_cost_base || 0).toLocaleString()}
                                        </div>
                                        <div className="text-muted" style={{fontSize:'10px'}}>
                                            Pagado: {(t.provider_currency_code || t.local_currency_code || 'CLP') === 'CLP' 
                                                ? `$${Math.round(t.driver_earnings || 0).toLocaleString()}` 
                                                : `${Math.round(t.driver_earnings || 0).toLocaleString()} ${t.provider_currency_code || t.local_currency_code || 'CLP'}`}
                                        </div>
                                    </td>
                                    <td>
                                        {t.settlement_id ? (
                                            <Badge bg="success" className="p-2" style={{fontSize:'11px'}}>LIQ #{t.settlement_id}</Badge>
                                        ) : (
                                            <InputGroup size="sm" style={{width: '130px'}}>
                                                <Form.Control 
                                                    type="number"
                                                    placeholder="Nº Liq"
                                                    value={manualSettlements[t.id] || ''}
                                                    onChange={e => setManualSettlement({...manualSettlements, [t.id]: e.target.value})}
                                                />
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => handleManualLink(t.id)}
                                                    disabled={actionLoading[t.id]}
                                                >
                                                    {actionLoading[t.id] ? <Spinner animation="border" size="sm"/> : <LinkIcon size={14}/>}
                                                </Button>
                                            </InputGroup>
                                        )}
                                    </td>
                                    <td>
                                        {t.client_invoice_id ? (
                                            <Badge bg="purple" className="p-2" style={{backgroundColor:'#6f42c1', fontSize:'11px'}}>
                                                {t.client_invoice?.internal_number || `PREF #${t.client_invoice_id}`}
                                            </Badge>
                                        ) : (
                                            <InputGroup size="sm" style={{width: '130px'}}>
                                                <Form.Control 
                                                    type="text"
                                                    placeholder="Nº Pre"
                                                    value={manualInvoices[t.id] || ''}
                                                    onChange={e => setManualInvoices({...manualInvoices, [t.id]: e.target.value})}
                                                />
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => handleManualInvoiceLink(t.id)}
                                                    disabled={actionLoading[t.id]}
                                                    style={{borderColor: '#6f42c1', color: '#6f42c1'}}
                                                >
                                                    {actionLoading[t.id] ? <Spinner animation="border" size="sm"/> : <LinkIcon size={14}/>}
                                                </Button>
                                            </InputGroup>
                                        )}
                                    </td>
                                    <td className="text-end px-3">
                                        {(t.settlement_id || t.client_invoice_id) && (
                                            <Button 
                                                variant="outline-warning" 
                                                size="sm" 
                                                className="py-1 fw-bold"
                                                onClick={() => { setSelectedTrip(t); setShowReleaseModal(true); }}
                                            >
                                                <Unlock size={14} className="me-1" /> Liberar
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* MODAL DE ADVERTENCIA */}
            <Modal show={showReleaseModal} onHide={() => setShowReleaseModal(false)} centered>
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title className="h5 fw-bold"><AlertCircle size={20} className="me-2" /> Liberar Reserva</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4 text-center">
                    <p>¿Desea <strong>QUITAR TODOS LOS VÍNCULOS</strong> de la reserva <span className="text-primary fw-bold">#{selectedTrip?.id}</span>?</p>
                    <div className="bg-light p-3 rounded border border-warning small text-start">
                        {selectedTrip?.settlement_id ? (
                            <div className="mb-1">- Se borra el vínculo con la <strong>Liq #{selectedTrip.settlement_id}</strong>.</div>
                        ) : null}
                        {selectedTrip?.client_invoice_id ? (
                            <div className="mb-1">
                                - Se borra el vínculo con la <strong>Pre-Factura {
                                    selectedTrip.client_invoice?.internal_number 
                                        ? (selectedTrip.client_invoice.internal_number.startsWith('#') || selectedTrip.client_invoice.internal_number.toUpperCase().startsWith('PRE')
                                            ? selectedTrip.client_invoice.internal_number
                                            : `#${selectedTrip.client_invoice.internal_number}`)
                                        : `PREF #${selectedTrip.client_invoice_id}`
                                }</strong>.
                            </div>
                        ) : null}
                        <div>- La reserva quedará habilitada para edición inmediata.</div>
                        <div>- Los totales de liquidación y facturación se recalcularán solos.</div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={() => setShowReleaseModal(false)}>Cancelar</Button>
                    <Button variant="warning" className="fw-bold" onClick={handleRelease}>Confirmar Liberación</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TripAdminView;
